import React, { useMemo } from "react";
import {
  TrendingDown,
  TrendingUp,
  Smile,
  Smartphone,
  Monitor,
  Gauge as GaugeIcon,
} from "lucide-react";
import styles from "./SitePerformanceViewer.module.css";
import uiStyles from "../../reportViewers/ReportUIPrimitives.module.css";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const splitBulletsSmart = (text = "") => {
  const t = (text || "").trim();
  if (!t) return [];

  if (t.includes("•")) {
    return t
      .split(/\n?\s*•\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [t.replace(/\s+/g, " ").trim()];

  // guard: only split on bullet char, otherwise keep as single line
};

const labelToPattern = (label = "") =>
  String(label || "")
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/\s+/g, "\\s+");

const extractSection = (md, startLabel, endLabel) => {
  const startPattern = labelToPattern(startLabel);
  const endPattern = endLabel ? labelToPattern(endLabel) : null;

  const start = md.search(new RegExp(`(^|\\n)\\s*${startPattern}\\s*:`, "i"));
  if (start === -1) return "";

  const sliced = md.slice(start);

  const end = endLabel
    ? sliced.search(new RegExp(`\\n\\s*${endPattern}\\s*:`, "i"))
    : -1;

  const section = end === -1 ? sliced : sliced.slice(0, end);

  return section
    .replace(new RegExp(`(^|\\n)\\s*${startPattern}\\s*:\\s*`, "i"), "")
    .trim();
};

const normalizeSitePerformanceMarkdown = (md = "") => {
  const s = String(md || "").replace(/\r\n/g, "\n");
  return s
    .replace(/(^|\n)\s*Mobile Summary\s*:/gi, "$1MOBILE_SUMMARY:")
    .replace(/(^|\n)\s*Desktop Summary\s*:/gi, "$1DESKTOP_SUMMARY:")
    .replace(/(^|\n)\s*Pages\s*:/gi, "$1PAGES_SUMMARY:")
    .replace(/(^|\n)\s*Performance Summary\s*:/gi, "$1PERFORMANCE_SUMMARY:")
    .replace(/(^|\n)\s*Performance\s*:/gi, "$1PERFORMANCE_SUMMARY:")
    .replace(/(^|\n)\s*Final Summary\s*:/gi, "$1FINAL_SUMMARY:");
};

const parseSitePerformance = (md = "") => {
  const raw = normalizeSitePerformanceMarkdown(md);

  const getNum = (pattern) => {
    const m = raw.match(pattern);
    if (!m) return null;
    const v = parseFloat(m[1]);
    return Number.isNaN(v) ? null : v;
  };

  const mobile = {
    performance: getNum(/Mobile:[\s\S]*?Performance:\s*([\d.]+)/i),
    accessibility: getNum(/Mobile:[\s\S]*?Accessibility:\s*([\d.]+)/i),
    bestPractices: getNum(/Mobile:[\s\S]*?Best Practices:\s*([\d.]+)/i),
    seo: getNum(/Mobile:[\s\S]*?SEO:\s*([\d.]+)/i),
  };

  const desktop = {
    performance: getNum(/Desktop:[\s\S]*?Performance:\s*([\d.]+)/i),
    accessibility: getNum(/Desktop:[\s\S]*?Accessibility:\s*([\d.]+)/i),
    bestPractices: getNum(/Desktop:[\s\S]*?Best Practices:\s*([\d.]+)/i),
    seo: getNum(/Desktop:[\s\S]*?SEO:\s*([\d.]+)/i),
  };

  const hasMobile = mobile.performance != null;
  const hasDesktop = desktop.performance != null;
  if (!hasMobile && !hasDesktop) return null;

  // Keep blocks tight so they can't swallow later sections
  const mobileBlock = (() => {
    const m = raw.match(
      /Mobile:\s*[\s\S]*?(?=\n\s*Desktop:|\n\s*PAGES_SUMMARY:|\n\s*PERFORMANCE_SUMMARY:|\n\s*FINAL_SUMMARY:|$)/i,
    );
    return m ? m[0] : "";
  })();

  const desktopBlock = (() => {
    const m = raw.match(
      /Desktop:\s*[\s\S]*?(?=\n\s*PAGES_SUMMARY:|\n\s*PERFORMANCE_SUMMARY:|\n\s*FINAL_SUMMARY:|$)/i,
    );
    return m ? m[0] : "";
  })();

  // EXACT labels we render in the UI
  const mobileSummary =
    extractSection(mobileBlock, "MOBILE_SUMMARY", "Desktop") ||
    extractSection(raw, "MOBILE_SUMMARY", "Desktop");

  const desktopSummary =
    extractSection(desktopBlock, "DESKTOP_SUMMARY", "PAGES_SUMMARY") ||
    extractSection(raw, "DESKTOP_SUMMARY", "PAGES_SUMMARY");

  const pagesText =
    extractSection(raw, "PAGES_SUMMARY", "PERFORMANCE_SUMMARY") ||
    extractSection(raw, "PAGES_SUMMARY", "FINAL_SUMMARY");

  const performanceIdeasText = extractSection(
    raw,
    "PERFORMANCE_SUMMARY",
    "FINAL_SUMMARY",
  );

  const finalSummary = extractSection(raw, "FINAL_SUMMARY", null);

  const result = {
    mobile: hasMobile ? mobile : null,
    desktop: hasDesktop ? desktop : null,
    mobileSummary,
    desktopSummary,
    pagesText,
    performanceIdeasText,
    finalSummary,
  };
  return result;
};

const Gauge = ({ value = 0, variant = "mobile" }) => {
  const v = clamp(Number(value) || 0, 0, 100);

  const cap = 12;
  const scoreLen = clamp(v, 0, 100 - cap);

  return (
    <div className={styles.gaugeWrap} data-variant={variant}>
      <svg className={styles.gaugeSvg} viewBox="0 0 128 80" aria-hidden="true">
        <path
          className={styles.gaugeTrack}
          d="M12 68 A52 52 0 0 1 116 68"
          pathLength="100"
        />

        <path
          className={styles.gaugeScore}
          d="M12 68 A52 52 0 0 1 116 68"
          pathLength="100"
          style={{ strokeDasharray: `${scoreLen} ${100 - scoreLen}` }}
        />

        <path
          className={styles.gaugeCap}
          d="M12 68 A52 52 0 0 1 116 68"
          pathLength="100"
          style={{
            strokeDasharray: `${cap} ${100 - cap}`,
            strokeDashoffset: -(100 - cap),
          }}
        />
      </svg>

      <div className={styles.gaugeCenter}>
        <div className={styles.deviceIcon} aria-hidden="true">
          {variant === "desktop" ? (
            <Monitor size={28} strokeWidth={1.8} />
          ) : (
            <Smartphone size={28} strokeWidth={1.8} />
          )}
        </div>
      </div>
    </div>
  );
};

const scoreColor = (v) => {
  if (v >= 90) return "#22c55e";
  if (v >= 50) return "#f59e0b";
  return "#ef4444";
};

const SCORE_LABELS = [
  { key: "performance", label: "Performance" },
  { key: "accessibility", label: "Accessibility" },
  { key: "bestPractices", label: "Best Practices" },
  { key: "seo", label: "SEO" },
];

const ScoreBars = ({ scores }) => {
  if (!scores) return null;
  return (
    <div className={styles.scoreBars}>
      {SCORE_LABELS.map(({ key, label }) => {
        const v = scores[key];
        if (v == null) return null;
        const color = scoreColor(v);
        return (
          <div key={key} className={styles.scoreBarRow}>
            <span className={styles.scoreBarLabel}>{label}</span>
            <div className={styles.scoreBarTrack}>
              <div
                className={styles.scoreBarFill}
                style={{ width: `${clamp(v, 0, 100)}%`, background: color }}
              />
            </div>
            <span className={styles.scoreBarValue} style={{ color }}>
              {v}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const InfoRow = ({ text, tone = "mobile" }) => {
  if (!text) return null;
  return (
    <div className={styles.infoRow} data-tone={tone}>
      <span className={styles.infoIconWrap} aria-hidden="true">
        <Smile className={styles.infoIcon} />
      </span>
      <p className={styles.infoText}>{text}</p>
    </div>
  );
};

const TextSection = ({ title, text }) => {
  const bullets = useMemo(() => splitBulletsSmart(text), [text]);
  if (!bullets.length) return null;

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      <div className={styles.sectionBody}>
        {bullets.map((b, idx) => (
          <div key={`${title}-${idx}`} className={styles.sectionRow}>
            <span className={uiStyles.summaryCheck} aria-hidden="true" />
            <p className={styles.sectionRowText}>{b}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const SitePerformanceViewer = ({ markdown, title = "Core Web Vitals" }) => {
  console.log(
    "[SitePerf] RENDER called | markdown:",
    markdown
      ? `(${typeof markdown}, ${String(markdown).length} chars)`
      : markdown,
  );

  if (!markdown) {
    console.log("[SitePerf] RENDER: markdown is falsy, returning null");
    return null;
  }

  const parsed = parseSitePerformance(markdown);

  if (!parsed) {
    console.log("[SitePerf] RENDER: parsed is null, showing fallback");
    return (
      <div className={styles.card}>
        <div className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <div className={styles.brandIcon} aria-hidden="true">
              <GaugeIcon className={styles.brandIconGlyph} />
            </div>
            <div className={styles.topBarTitle}>{title}</div>
          </div>
        </div>
        <div className={styles.fallbackText}>{markdown}</div>
      </div>
    );
  }

  const {
    mobile,
    desktop,
    topSummary,
    mobileSummary,
    desktopSummary,
    pagesText,
    performanceIdeasText,
    finalSummary,
  } = parsed;

  const mobileTrend = (mobile?.performance ?? 0) >= 50 ? "up" : "down";
  const desktopTrend = (desktop?.performance ?? 0) >= 50 ? "up" : "down";

  return (
    <div className={styles.card}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <div className={styles.brandIcon} aria-hidden="true">
            <GaugeIcon className={styles.brandIconGlyph} />
          </div>
          <div className={styles.topBarTitle}>{title}</div>
        </div>
        <div className={styles.topBarRight}>
          A snapshot of how your experience feels on real devices
        </div>
      </div>

      {topSummary ? (
        <div className={styles.topSummary}>
          <p className={styles.topSummaryText}>{topSummary}</p>
        </div>
      ) : null}

      <div className={styles.deviceGrid}>
        {mobile ? (
          <div className={styles.deviceCard}>
            <div className={styles.deviceCardHeader}>
              <div className={styles.deviceCardTitleRow}>
                <div className={styles.deviceCardTitle}>Mobile Experience</div>
                {mobileTrend === "up" ? (
                  <TrendingUp
                    className={styles.trendLucide}
                    data-trend="up"
                    size={16}
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                ) : (
                  <TrendingDown
                    className={styles.trendLucide}
                    data-trend="down"
                    size={16}
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>

            <Gauge value={mobile.performance} variant="mobile" />
            <InfoRow text={mobileSummary} tone="mobile" />
          </div>
        ) : null}

        {desktop ? (
          <div className={styles.deviceCard}>
            <div className={styles.deviceCardHeader}>
              <div className={styles.deviceCardTitleRow}>
                <div className={styles.deviceCardTitle}>Desktop Experience</div>
                {desktopTrend === "up" ? (
                  <TrendingUp
                    className={styles.trendLucide}
                    data-trend="up"
                    size={16}
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                ) : (
                  <TrendingDown
                    className={styles.trendLucide}
                    data-trend="down"
                    size={16}
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>

            <Gauge value={desktop.performance} variant="desktop" />
            <InfoRow text={desktopSummary} tone="desktop" />
          </div>
        ) : null}
      </div>

      <TextSection title="Performance Ideas" text={performanceIdeasText} />
      <TextSection title="Pages" text={pagesText} />
      <TextSection title="Summary" text={finalSummary} />
    </div>
  );
};

export default SitePerformanceViewer;
